<template>
  <header class="widget-header">
    <div class="title-group">
      <h3>My Calendar</h3>
      <span v-if="eventCount" class="event-count-badge">
        {{ eventCount }} upcoming
      </span>
      <span v-if="isOffline" class="offline-badge" title="Showing cached events from local storage">
        ⚡ Offline Mode
      </span>
    </div>

    <div class="header-actions">
      <button
        class="btn-icon-refresh"
        :disabled="loading"
        title="Refresh calendar"
        @click="$emit('retry')"
      >
        <svg
          :class="{ 'spin': loading }"
          xmlns="http://www.w3.org/2000/svg"
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M21.5 2v6h-6M2.5 22v-6h6" />
          <path d="M2 11.5a10 10 0 0 1 18.8-4.3L21.5 8M22 12.5a10 10 0 0 1-18.8 4.2L2.5 16" />
        </svg>
      </button>

      <button
        v-if="!hasError && !isOffline"
        class="btn-toggle"
        :disabled="loading"
        @click="$emit('toggle-form')"
      >
        {{ showForm ? 'Cancel' : '+ New Event' }}
      </button>
    </div>
  </header>
</template>

<script setup>
defineProps({
  eventCount: {
    type: Number,
    default: 0
  },
  isOffline: {
    type: Boolean,
    default: false
  },
  hasError: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  showForm: {
    type: Boolean,
    default: false
  },
});

defineEmits(['retry', 'toggle-form']);
</script>

<style lang="scss" scoped>
@use "sass:color";
@use "@/assets/styles/abstract/variables" as *;

.widget-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap; // Allows wrapping on tight viewports
  gap: 1rem;

  .title-group {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;

    h3 {
      margin: 0;
      font-size: 1.35rem;
      font-weight: 700;
      color: $orange;
      letter-spacing: -0.01em;
    }

    .event-count-badge {
      font-size: 0.75rem;
      font-weight: 700;
      background: color.mix($orange, #ffffff, 15%);
      color: $orange;
      padding: 0.15rem 0.5rem;
      border-radius: 12px;
    }

    .offline-badge {
      font-size: 0.72rem;
      font-weight: 700;
      background: #fef3c7;
      color: #d97706;
      border: 1px solid #fde68a;
      padding: 0.15rem 0.5rem;
      border-radius: 12px;
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .btn-icon-refresh {
      background: #f8fafc;
      border: 1px solid color.mix($light-brown, $secondary-color, 60%);
      color: $dark-blue;
      border-radius: 8px;
      padding: 0.45rem;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover:not(:disabled) {
        background: $dark-blue;
        color: $white;
      }

      .spin {
        animation: spin 0.8s linear infinite;
      }
    }

    .btn-toggle {
      background: #f8fafc;
      border: 1px solid color.mix($light-brown, $secondary-color, 60%);
      color: $dark-blue;
      border-radius: 8px;
      padding: 0.45rem 1rem;
      font-size: 0.9rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover:not(:disabled) {
        background: $dark-blue;
        color: $white;
        border-color: $dark-blue;
        box-shadow: 0 4px 12px rgba(21, 76, 140, 0.25);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }

  // Mobile layout adjustment
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;

    .title-group {
      justify-content: space-between;
      width: 100%;
    }

    .header-actions {
      width: 100%;
      justify-content: flex-end;
    }
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>